// lineChart.ts

type Padding = { top: number; right: number; bottom: number; left: number }

export interface Series {
	name: string
	color: string // CSS color
	data: number[]
}

export interface ChartConfig {
	width?: number
	height?: number
	padding?: Padding
	title?: string
	xLabel?: string // 가로축 라벨 (값 축)
	yLabel?: string // 세로축 라벨 (카테고리 축)
	xCategories: string[] // ex) ["데이터 1", ...]  ← 카테고리(세로축) 라벨
	series: Series[] // ex) [{ name, color, data }]
	gridLines?: number // 세로 그리드 수(값 축)
	showArea?: boolean // (미사용) 세로차트 호환을 남겨둠
}

export function renderRowBarChartSVG(mountTarget: string | HTMLElement, cfg: ChartConfig): void {
	const mount = typeof mountTarget === 'string' ? document.querySelector<HTMLElement>(mountTarget) : mountTarget
	if (!mount) throw new Error('Mount element not found')

	const {
		width = 960,
		height = 400,
		padding = { top: 40, right: 40, bottom: 60, left: 120 }, // 왼쪽 라벨 공간 늘림
		title = 'Horizontal bar chart',
		xLabel = '(값 축)',
		yLabel = '(카테고리)',
		xCategories, // ← 세로축(카테고리) 라벨
		series,
		gridLines = 6,
	} = cfg

	// 초기화
	mount.innerHTML = ''
	mount.style.position = 'relative'

	const svgNS = 'http://www.w3.org/2000/svg'
	const svg = createEl('svg', {
		width: String(width),
		height: String(height),
		viewBox: `0 0 ${width} ${height}`,
		style: 'background:#fff;border-radius:4px;',
	})

	// (애니메이션 CSS 주입: 왼→오 채움)
	const style = document.createElement('style')
	style.textContent = `
    .bar-h {
      transition: width .7s cubic-bezier(.2,.7,0,1);
    }
  `
	mount.appendChild(style)

	const root = createEl('g')
	svg.appendChild(root)

	// 제목
	root.appendChild(
		createEl(
			'text',
			{
				x: '20',
				y: '26',
				fill: '#6B6E79',
				'font-size': '20',
				'font-weight': '700',
				'font-family': 'ui-sans-serif, -apple-system, "Noto Sans KR", Arial',
			},
			title,
		),
	)

	// 플롯 영역
	const plotX = padding.left
	const plotY = padding.top
	const plotW = width - padding.left - padding.right
	const plotH = height - padding.top - padding.bottom
	const extraTop = 80
	const plotHAdj = plotH - extraTop

	const yCount = Math.max(xCategories.length, 1)

	// 값 스케일 (가로축: 0..yMax → 0..plotW)
	const allVals = series.flatMap(s => s.data.filter(v => typeof v === 'number'))
	const xMaxData = Math.max(allVals.length ? Math.max(...allVals) : 10, 1)
	const xMax = niceMax(xMaxData)
	const xMin = 0
	const xScale = (v: number) => plotX + (v - xMin) * (plotW / (xMax - xMin))

	// 카테고리(세로축) 밴드 스케일
	const bandStep = plotHAdj / yCount // 각 카테고리 밴드 높이
	const bandPadding = 0.1 // 밴드 안쪽 여백(비율)
	const bandInner = bandStep * (1 - bandPadding) // 실제 그릴 수 있는 높이
	const gap = 6 // 시리즈 간 간격(px)
	const barH = (bandInner - gap * (series.length + 1)) / Math.max(series.length, 1)
	const bandTop = (i: number) => plotY + i * bandStep + (bandStep - bandInner) / 2

	const graphPlotY = plotY + extraTop
	const graphBandTop = (i: number) => graphPlotY + i * bandStep + (bandStep - bandInner) / 2

	// 세로 그리드 + x축 눈금 (값 축)
	const effectiveGridLines = Math.min(gridLines, xMax)
	const grid = createEl('g')
	root.appendChild(grid)
	for (let i = 0; i <= effectiveGridLines; i++) {
		const v = xMax * (i / effectiveGridLines)
		const x = xScale(v)
		grid.appendChild(
			createEl('line', {
				x1: String(x),
				y1: String(graphPlotY),
				x2: String(x),
				y2: String(graphPlotY + plotHAdj),
				stroke: '#DDE1E7',
				'stroke-dasharray': '4 4',
			}),
		)
		grid.appendChild(
			createEl(
				'text',
				{
					x: String(x),
					y: String(graphPlotY - 10),
					'text-anchor': 'middle',
					fill: '#6B6E79',
					'font-size': '12',
					'font-family': 'ui-sans-serif, "Noto Sans KR", Arial',
				},
				String(Math.round(v)),
			),
		)
	}

	// 축 라벨
	root.appendChild(
		createEl(
			'text',
			{
				x: String(plotX + plotW),
				y: String(graphPlotY + plotHAdj + 20),
				'text-anchor': 'end',
				fill: '#6B6E79',
				'font-size': '12',
				'font-weight': '600',
			},
			xLabel,
		),
	)
	root.appendChild(
		createEl(
			'text',
			{
				x: String(20),
				y: String(graphPlotY - 30),
				fill: '#6B6E79',
				'font-size': '12',
				'font-weight': '600',
			},
			yLabel,
		),
	)

	// 카테고리 라벨 + 가는 보조선(가독용)
	xCategories.forEach((lab, i) => {
		const yB = graphBandTop(i) + bandInner / 2
		// 카테고리 라벨(왼쪽)
		root.appendChild(
			createEl(
				'text',
				{
					x: String(plotX - 10),
					y: String(yB - 10),
					'text-anchor': 'end',
					fill: '#111827',
					'font-size': '12',
					'font-family': 'ui-sans-serif, "Noto Sans KR", Arial',
				},
				lab,
			),
		)
		// 얇은 가이드 라인(선택)
		root.appendChild(
			createEl('line', {
				x1: String(plotX),
				y1: String(yB + 30),
				x2: String(plotX + plotW),
				y2: String(yB + 30),
				stroke: '#DDE1E7',
				'stroke-dasharray': '4 4',
			}),
		)
	})

	// 범례
	const legend = createEl('g')
	root.appendChild(legend)
	let lx = 20
	const ly = 48
	series.forEach((s, idx) => {
		const group = createEl('g')
		legend.appendChild(group)
		group.appendChild(
			createEl('rect', {
				x: String(lx),
				y: String(ly - 10),
				width: '14',
				height: '14',
				rx: '3',
				fill: s.color,
			}),
		)
		group.appendChild(
			createEl(
				'text',
				{
					x: String(lx + 20),
					y: String(ly + 2),
					fill: '#374151',
					'font-size': '12',
				},
				s.name ?? `시리즈${idx + 1}`,
			),
		)
		lx += 90
	})

	// 좌측 세로축 (baseline)
	root.appendChild(
		createEl('line', {
			x1: String(plotX),
			y1: String(graphPlotY),
			x2: String(plotX),
			y2: String(graphPlotY + plotHAdj),
			stroke: '#6B6E79',
		}),
	)
	root.appendChild(
		createEl('line', {
			x1: String(plotX),
			y1: String(graphPlotY),
			x2: String(plotX + plotW),
			y2: String(graphPlotY),
			stroke: '#6B6E79',
		}),
	)

	// ===== 막대들(그룹형, 왼→오 애니메이션: width 0 → target) =====
	xCategories.forEach((_, ci) => {
		const yGroupTop = graphBandTop(ci)
		series.forEach((s, si) => {
			const v = s.data[ci]
			if (typeof v !== 'number') return
			const w = Math.max(0, xScale(v) - plotX)
			const y = yGroupTop + gap * (si + 1) + barH * si
			const rect = createEl('rect', {
				x: String(plotX),
				y: String(y),
				width: '0', // 초기 0 → 애니메이션으로 늘림
				height: String(barH),
				rx: '4',
				fill: s.color,
				class: 'bar-h',
			})
			root.appendChild(rect)
			// 다음 프레임에 최종 width로 전환 → 트랜지션 발동
			requestAnimationFrame(() => {
				rect.setAttribute('width', String(w))
			})
			// Tooltip 이벤트
			const seriesName = s?.name ?? `시리즈${si + 1}`
			const categoryLabel = xCategories[ci] ?? `데이터 ${ci + 1}`
			const value = v
			;(rect as any).dataset.series = String(si)
			;(rect as any).dataset.categoryIndex = String(ci)
			;(rect as any).dataset.value = String(value)
			rect.addEventListener('mouseenter', (e: MouseEvent) => {
				showTooltip(`<div style="font-weight:700;margin-bottom:2px;">${categoryLabel}</div><div>${seriesName}: <b>${value}</b></div>`)
				moveTooltip(e)
			})
			rect.addEventListener('mousemove', (e: MouseEvent) => moveTooltip(e))
			rect.addEventListener('mouseleave', () => hideTooltip())
		})
	})

	mount.appendChild(svg)

	// ======= Tooltip =======
	const tooltip = document.createElement('div')
	tooltip.style.position = 'absolute'
	tooltip.style.zIndex = '10'
	tooltip.style.pointerEvents = 'none'
	tooltip.style.padding = '6px 8px'
	tooltip.style.background = '#111827'
	tooltip.style.color = '#fff'
	tooltip.style.borderRadius = '4px'
	tooltip.style.fontSize = '12px'
	tooltip.style.lineHeight = '1.2'
	tooltip.style.whiteSpace = 'nowrap'
	tooltip.style.boxShadow = '0 4px 12px rgba(0,0,0,0.18)'
	tooltip.style.opacity = '0'
	mount.appendChild(tooltip)

	const showTooltip = (html: string) => {
		tooltip.innerHTML = html
		tooltip.style.opacity = '1'
	}
	const moveTooltip = (e: MouseEvent) => {
		const r = mount.getBoundingClientRect()
		let x = e.clientX - r.left + 8
		let y = e.clientY - r.top - 12
		tooltip.style.left = '0px'
		tooltip.style.top = '-1000px'
		const tw = tooltip.offsetWidth || 0
		const th = tooltip.offsetHeight || 0
		if (x + tw + 8 > r.width) x = r.width - tw - 8
		if (x < 4) x = 4
		if (y < 4) y = e.clientY - r.top + 16
		if (y + th + 4 > r.height) y = r.height - th - 4
		tooltip.style.left = `${x}px`
		tooltip.style.top = `${y}px`
	}
	const hideTooltip = () => {
		tooltip.style.opacity = '0'
	}

	// ======= 내부 유틸 =======
	function createEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, text?: string): SVGElementTagNameMap[K] {
		const el = document.createElementNS(svgNS, tag) as SVGElementTagNameMap[K]
		Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v))
		if (text != null) el.textContent = text
		return el
	}

	function niceMax(v: number): number {
		const pow = Math.pow(10, Math.floor(Math.log10(v)))
		const n = v / pow
		const step = n <= 2 ? 2 : n <= 5 ? 5 : 10
		return step * pow
	}
}

/* ================= 예시 호출 =================
renderBarChartSVG("#chart", {
  title: "제품별 월간 매출",
  xLabel: "(매출, 만원)",
  yLabel: "(제품)",
  xCategories: ["A","B","C","D","E","F","G"],
  series: [
    { name: "2024", color: "#22C1D6", data: [30, 22, 40, 18, 35, 28, 14] },
    { name: "2025", color: "#A78BFA", data: [36, 25, 32, 26, 42, 30, 20] }
  ],
  gridLines: 6
});
================================================ */
